import { memo, useEffect, useMemo, useState } from "react";
import { Minus, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";

function clamp(value, min, max) {
  if (value === "" || value === null || Number.isNaN(value)) {
    return "";
  }

  let next = Number(value);

  if (min !== undefined) next = Math.max(next, min);
  if (max !== undefined) next = Math.min(next, max);

  return next;
}

function BaseNumericInput({
  id,

  value,
  onChange,

  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,

  slider = true,

  presets = [],

  formatter = (v) => String(v ?? ""),
  parser = (v) => Number(v),

  inputMode = "decimal",

  placeholder,

  suffix,

  prefix,
}) {
  const [text, setText] = useState(formatter(value));

  useEffect(() => {
    setText(formatter(value));
  }, [value, formatter]);

  const numericValue = useMemo(() => {
    if (value === "" || value === null || value === undefined) {
      return min;
    }

    return Number(value);
  }, [value, min]);

  const commit = (raw) => {
    if (raw === "") {
      setText("");
      onChange("");
      return;
    }

    const parsed = parser(raw);

    if (Number.isNaN(parsed)) {
      setText(formatter(value));
      return;
    }

    const next = clamp(parsed, min, max);

    onChange(next);

    setText(formatter(next));
  };

  const increment = () => {
    commit(Number(value || 0) + step);
  };

  const decrement = () => {
    commit(Number(value || 0) - step);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="icon" onClick={decrement}>
          <Minus className="h-4 w-4" />
        </Button>

        <div className="relative flex-1">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {prefix}
            </span>
          )}

          <Input
            id={id}
            value={text}
            placeholder={placeholder}
            inputMode={inputMode}
            autoComplete="off"
            spellCheck={false}
            className={prefix ? "pl-8" : ""}
            onChange={(e) => {
              setText(e.target.value);
            }}
            onBlur={() => commit(text)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
                return;
              }

              if (e.key === "ArrowUp") {
                e.preventDefault();
                increment();
                return;
              }

              if (e.key === "ArrowDown") {
                e.preventDefault();
                decrement();
                return;
              }
            }}
          />

          {suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {suffix}
            </span>
          )}
        </div>

        <Button type="button" variant="outline" size="icon" onClick={increment}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {slider && (
        <Slider
          value={[numericValue]}
          min={min}
          max={max}
          step={step}
          onValueChange={([next]) => commit(next)}
        />
      )}

      {presets.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <Button
              key={preset.value}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => commit(preset.value)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export default memo(BaseNumericInput);
